import { ApolloLink, Observable } from '@apollo/client';
import { makeExecutableSchema } from '@graphql-tools/schema';
import { graphql, print } from 'graphql';
import schemaText from '../../schemas.gql';
import isIndexedDBError from './isIndexedDBError';
import resolvers from './resolvers';
// Build schema
let schema = null;
function buildOfflineSchema() {
    if (!schema) {
        schema = makeExecutableSchema({
            typeDefs: schemaText,
            resolvers,
        });
    }
    return schema;
}
function getIndexedDBFailure(error) {
    if (isIndexedDBError(error)) {
        return error;
    }
    if (error != null &&
        typeof error === 'object' &&
        'originalError' in error &&
        isIndexedDBError(error.originalError)) {
        return error.originalError;
    }
    return null;
}
// Create offline server link. When IndexedDB/Dexie flakes, forward to the next
// link (network) instead of failing the operation.
const offlineServerLink = new ApolloLink((operation, forward) => {
    return new Observable((observer) => {
        let forwardSub;
        (async () => {
            const { query, variables, operationName } = operation;
            const fallbackToNetwork = (error) => {
                console.warn('[offlineServerLink] IndexedDB error, falling back to network', {
                    operationName,
                    error,
                });
                if (!forward) {
                    observer.error(error);
                    return;
                }
                forwardSub = forward(operation).subscribe(observer);
            };
            try {
                const offlineSchema = buildOfflineSchema();
                // Convert the query to a string to ensure fragments are resolved
                const queryString = print(query);
                const result = await graphql({
                    schema: offlineSchema,
                    source: queryString,
                    variableValues: variables,
                    operationName,
                });
                if (result.errors && result.errors.length > 0) {
                    const indexedDBFailure = result.errors
                        .map(err => getIndexedDBFailure(err) ?? getIndexedDBFailure(err.originalError))
                        .find(Boolean);
                    if (indexedDBFailure) {
                        fallbackToNetwork(indexedDBFailure);
                        return;
                    }
                    observer.error(new Error(result.errors[0].message));
                }
                else {
                    observer.next(result);
                    observer.complete();
                }
            }
            catch (error) {
                if (getIndexedDBFailure(error)) {
                    fallbackToNetwork(error);
                    return;
                }
                console.error('[offlineServerLink] error', {
                    query,
                    variables,
                    operationName,
                    error,
                });
                observer.error(error);
            }
        })();
        return () => {
            forwardSub?.unsubscribe();
        };
    });
});
export default offlineServerLink;
